// ./src/components/features/Vehicle/UnitToggle/UnitToggle.tsx
import React from "react";
import { UnstyledButton, Text, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  UnitDisplayMode,
  formatUnitLabel,
  getSwitchLabel,
} from "@/utils/unitDisplayHelpers";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface UnitToggleProps {
  /** Current display mode */
  mode: UnitDisplayMode;

  /** Current distance unit being displayed */
  distanceUnit: string;

  /** Current volume unit being displayed */
  volumeUnit: string;

  /** Callback when mode is toggled */
  onToggle: () => void;

  /** Size variant */
  size?: "sm" | "md";

  /** Custom class name */
  className?: string;
}

// ============================================
// Constants
// ============================================

const SIZE_CONFIG = {
  sm: {
    fontSize: "xs" as const,
    iconSize: 10,
    paddingX: 6,
    paddingY: 2,
    gap: 4,
  },
  md: {
    fontSize: "xs" as const,
    iconSize: 12,
    paddingX: 8,
    paddingY: 3,
    gap: 4,
  },
};

// ============================================
// Component
// ============================================

/**
 * A small toggle button that switches between user and car unit preferences.
 * Shows current units (e.g., "km · L") with a swap icon.
 * 
 * Only render this component when units actually differ between user and car preferences.
 */
export const UnitToggle: React.FC<UnitToggleProps> = ({
  mode,
  distanceUnit,
  volumeUnit,
  onToggle,
  size = "sm",
  className,
}) => {
  const { t } = useTranslation();
  const config = SIZE_CONFIG[size];

  const label = formatUnitLabel(distanceUnit, volumeUnit);
  const tooltipText = getSwitchLabel(mode, t);

  // Visual indicator for mode
  const modeIndicator = mode === "user" ? "👤" : "🚗";

  return (
    <Tooltip label={tooltipText} withArrow position="bottom">
      <UnstyledButton
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: config.gap,
          padding: `${config.paddingY}px ${config.paddingX}px`,
          borderRadius: 4,
          backgroundColor: "var(--mantine-color-gray-1)",
          border: "1px solid var(--mantine-color-gray-3)",
          cursor: "pointer",
          transition: "all 150ms ease",
          minHeight: 24, // Ensure touch target
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "var(--mantine-color-gray-2)";
          e.currentTarget.style.borderColor = "var(--mantine-color-gray-4)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "var(--mantine-color-gray-1)";
          e.currentTarget.style.borderColor = "var(--mantine-color-gray-3)";
        }}
      >
        <Text
          size={config.fontSize}
          fw={500}
          c="dimmed"
          style={{ lineHeight: 1 }}
        >
          {modeIndicator}
        </Text>
        <Text
          size={config.fontSize}
          fw={500}
          ff="var(--ff-mono)"
          c="dimmed"
          style={{ lineHeight: 1, whiteSpace: "nowrap" }}
        >
          {label}
        </Text>
        <AppIcon
          iconName="ArrowsRightLeft"
          size={config.iconSize}
          stroke={2}
          color="var(--mantine-color-dimmed)"
        />
      </UnstyledButton>
    </Tooltip>
  );
};
